import { ANONYMOUS, loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { useEffect, useRef, useState } from "react";

const clientKey = process.env.REACT_APP_TOSS_WIDGET_CLIENT_KEY;

export const PaymentWidget = ({
  amount,
  orderId: orderIdProp,
  orderName,
  customerName,
  customerEmail,
} = {}) => {
  const amountCurrency = amount.currency;
  const amountValue = amount.value;
  const [widgets, setWidgets] = useState(null);
  const [ready, setReady] = useState(false);
  const paymentMethodWidgetRef = useRef(null);
  const paymentUiRenderedRef = useRef(false);

  useEffect(() => {
    async function fetchPaymentWidgets() {
      const tossPayments = await loadTossPayments(clientKey);
      const widgets = tossPayments.widgets({ customerKey: ANONYMOUS });
      setWidgets(widgets);
    }

    fetchPaymentWidgets();
  }, [clientKey]);

  useEffect(() => {
    async function renderPaymentWidgets() {
      if (widgets == null) {
        return;
      }
      /**
       * 위젯의 결제금액을 결제하려는 금액으로 초기화하세요.
       * renderPaymentMethods, renderAgreement, requestPayment 보다 반드시 선행되어야 합니다.
       * @docs https://docs.tosspayments.com/sdk/v2/js#widgetssetamount
       */
      await widgets.setAmount({
        currency: amountCurrency,
        value: amountValue,
      });

      if (!paymentUiRenderedRef.current) {
        paymentUiRenderedRef.current = true;

        const [paymentMethodWidget] = await Promise.all([
          /**
           * 결제창을 렌더링합니다.
           * @docs https://docs.tosspayments.com/sdk/v2/js#widgetsrenderpaymentmethods
           */
          widgets.renderPaymentMethods({
            selector: "#payment-method",
            // 렌더링하고 싶은 결제 UI의 variantKey
            // 결제 수단 및 스타일이 다른 멀티 UI를 직접 만들고 싶다면 계약이 필요해요.
            // @docs https://docs.tosspayments.com/guides/v2/payment-widget/admin#새로운-결제-ui-추가하기
            variantKey: "DEFAULT",
          }),
          /**
           * 약관을 렌더링합니다.
           * @docs https://docs.tosspayments.com/reference/widget-sdk#renderagreement선택자-옵션
           */
          widgets.renderAgreement({
            selector: "#agreement",
            variantKey: "AGREEMENT",
          }),
        ]);

        /**
         * 결제수단 선택 이벤트를 등록합니다.
         * @docs https://docs.tosspayments.com/sdk/v2/js#paymentmethodwidgeton
         */
        paymentMethodWidget.on(
          "paymentMethodSelect",
          (selectedPaymentMethod) => {
            // console.log("selectedPaymentMethod: ", selectedPaymentMethod);

            if (selectedPaymentMethod.code === "CARD") {
              // 카드 안내사항 노출
            }
            if (selectedPaymentMethod.code === "문화바우처") {
              // 커스텀 결제수단 (결제위젯 Pro 플랜 기능)
              // 문화바우처 안내사항 노출
            }
          }
        );

        paymentMethodWidgetRef.current = paymentMethodWidget;
        setReady(true);
      }
    }

    renderPaymentWidgets();
  }, [widgets, amountCurrency, amountValue]);

  return (
    <div className="wrapper w-100">
      <div className="max-w-540 w-100">
        <div id="payment-method" className="w-100" />
        <div id="agreement" className="w-100" />
        <div className="btn-wrapper w-100">
          <button
            type="button"
            disabled={!ready}
            className="w-full h-[44px] bg-[#3182F6] text-white font-semibold text-[1.6rem] rounded-[8px] disabled:opacity-50"
            onClick={async () => {
              try {
                /**
                 * 선택된 결제수단을 조회합니다.
                 * @docs https://docs.tosspayments.com/sdk/v2/js#paymentmethodwidgetgetselectedpaymentmethod
                 */
                const selectedPaymentMethod =
                  await paymentMethodWidgetRef.current?.getSelectedPaymentMethod();
                // console.log("selectedPaymentMethod: ", selectedPaymentMethod);

                /**
                 * 결제 요청
                 * 결제를 요청하기 전에 orderId, amount를 서버에 저장하세요.
                 * 결제 과정에서 악의적으로 결제 금액이 바뀌는 것을 확인하는 용도입니다.
                 * @docs https://docs.tosspayments.com/sdk/v2/js#widgetsrequestpayment
                 */
                await widgets?.requestPayment({
                  orderId: orderIdProp,
                  orderName,
                  ...(customerName ? { customerName } : {}),
                  ...(customerEmail ? { customerEmail } : {}),
                  successUrl:
                    window.location.origin +
                    "/payments/success" +
                    window.location.search,
                  failUrl:
                    window.location.origin +
                    "/payments/fail" +
                    window.location.search,
                });
              } catch (error) {
                console.error("결제 요청 실패: ", error);
              }
            }}
          >
            결제하기
          </button>
        </div>
      </div>
    </div>
  );
};
